import * as React from "react";

import { useNavigation } from "@/providers";
import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import { AssociationRecord } from "@/shared/domain/association";
import { MessagePathRecord, TopicRecord } from "@/shared/domain/topics";
import { getWorkspaceInput } from "@/shared/domain/workspaces/WorkspaceBuilder";
import { Goto } from "@/shared/navigation/Goto";
import { determineDefaultPanelType } from "@/shared/state/visualization/schema/v1";

interface MessagePathLinkProps {
  messagePath: MessagePathRecord;
  topic: TopicRecord;
  topicAssociation: AssociationRecord;
  linkPrefix?: string;
  className?: string;

  /**
   * If provided, the file will be opened at the given time.
   * Otherwise, the file will be opened at the start of the file.
   *
   * `startTime` is in nanoseconds since the UNIX epoch.
   **/
  startTime?: bigint;
}

export const MessagePathLink: React.FC<MessagePathLinkProps> = ({
  messagePath,
  topic,
  topicAssociation,
  linkPrefix,
  className,
  startTime,
}) => {
  const { goto } = useNavigation();

  const handleClick = (e: React.MouseEvent) => {
    // A click goes through goto.visualize, which hands the message path's panel
    // layout to the visualizer. The href, which carries no layout, serves
    // open-in-new-tab and copy-link.
    e.preventDefault();

    const panelType = determineDefaultPanelType(messagePath);

    goto.visualize({
      dataSource: {
        files: [topicAssociation.association_id],
      },
      params: {
        layout: getWorkspaceInput(panelType, topic, messagePath),
        t: startTime,
      },
    });
  };

  return (
    <RobotoLinkHoverUnderline
      to={Goto.createFileVisualizerRoute(
        topicAssociation.association_id,
        startTime,
      )}
      variant="body2"
      className={className}
      onClick={handleClick}
    >
      {linkPrefix}
      {messagePath.source_path}
    </RobotoLinkHoverUnderline>
  );
};
